import { useCallback, useEffect, useRef } from "react"
import { useReactFlow } from "@xyflow/react"
import dagre from "dagre"

function estimateDimensions(node) {
  if (node.type === 'actor') return { width: 150, height: 60 }
  if (node.type === 'hub') return { width: 100, height: 100 }
  if (node.type === 'decision') return { width: 150, height: 150 }
  if (node.type === 'external') return { width: 180, height: 70 }
  return { width: 172, height: 36 }
}

function computeLayout(nodes, edges, { rankdir = 'LR', nodesep = 40, ranksep = 80 } = {}) {
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({ rankdir, nodesep, ranksep, marginx: 50, marginy: 50 })

  const groupIds = new Set(nodes.filter(n => n.type === 'group').map(n => n.id))
  const childNodeIds = new Set(nodes.filter(n => n.parentId).map(n => n.id))
  const nodesToLayout = nodes.filter(n => !groupIds.has(n.id) && !childNodeIds.has(n.id))

  nodesToLayout.forEach((node) => {
    dagreGraph.setNode(node.id, estimateDimensions(node))
  })

  const layoutNodeIds = new Set(nodesToLayout.map(n => n.id))
  edges.forEach((edge) => {
    if (layoutNodeIds.has(edge.source) && layoutNodeIds.has(edge.target)) {
      dagreGraph.setEdge(edge.source, edge.target)
    }
  })

  dagre.layout(dagreGraph)

  return nodes.map((node) => {
    if (groupIds.has(node.id) || childNodeIds.has(node.id)) return node
    const nodeWithPosition = dagreGraph.node(node.id)
    const dims = estimateDimensions(node)
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - dims.width / 2,
        y: nodeWithPosition.y - dims.height / 2,
      },
    }
  })
}

/**
 * Applies dagre auto-layout on initial mount for generated flows (top-to-bottom),
 * and provides a manual auto-layout handler (left-to-right).
 */
export default function useAutoLayout(nodes, edges, setNodes, { isGenerated }) {
  const reactFlowInstance = useReactFlow()
  const autoLayoutApplied = useRef(false)

  // Auto-layout generated flows top-to-bottom on mount
  useEffect(() => {
    if (!isGenerated || autoLayoutApplied.current || nodes.length === 0) return
    autoLayoutApplied.current = true

    const layoutedNodes = computeLayout(nodes, edges, {
      rankdir: 'TB',
      nodesep: 60,
      ranksep: 100,
    })
    setNodes(layoutedNodes)

    setTimeout(() => {
      reactFlowInstance?.fitView({ padding: 0.2, duration: 400 })
    }, 50)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Manual auto-layout handler (left-to-right)
  const handleAutoLayout = useCallback(() => {
    const layoutedNodes = computeLayout(nodes, edges, {
      rankdir: 'LR',
      nodesep: 40,
      ranksep: 80,
    })
    setNodes(layoutedNodes)

    setTimeout(() => {
      reactFlowInstance?.fitView({ padding: 0.2, duration: 400 })
    }, 50)
  }, [nodes, edges, setNodes, reactFlowInstance])

  return { handleAutoLayout }
}
